// Discovery-flight guest waiver, signed on the CFI's phone (Casey, 10/20/26).
// The CFI opens it from a discovery booking and hands the phone over. One section per
// screen: a plain-English line, the full text, initials where they're required, then the
// signature. The doc write triggers onDiscoveryWaiverCreated, which builds the locked PDF,
// stamps the booking, and emails the guest a copy (CFI cc'd, Casey bcc'd).
//
// The owner can open it without a booking to try it out; that run is marked as a test.
//
// Fuel-included discovery flights (Casey, 10/26/26): after signing, the guest saves a card for the
// airplane on Stripe's hosted page. It's charged $170 per Hobbs hour, fuel included, when
// the CFI logs the flight (onFlightLogged -> chargeDiscoveryGuest). Opening this page for a
// booking that's already signed goes straight to the card step.
import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { addDoc, collection, doc, getDoc, onSnapshot, serverTimestamp } from 'firebase/firestore';
import { db } from '../../lib/firebase';
import { useAuth } from '../../lib/auth';
import type { BookingDoc } from '../../lib/types';
import { DEMO_BOOKINGS } from '../../lib/demoData';
import { SignaturePad, type SignatureResult } from '../../components/SignaturePad';
import WAIVER from '../../../functions/discoveryWaiver.json';

type Run = { t: string; b: boolean };

function Runs({ runs }: { runs: Run[] }) {
  return (
    <>
      {runs.map((r, i) => (r.b ? <strong key={i}>{r.t}</strong> : <span key={i}>{r.t}</span>))}
    </>
  );
}

function ageOn(dob: string, ref = new Date()) {
  const d = new Date(`${dob}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  let a = ref.getFullYear() - d.getFullYear();
  const m = ref.getMonth() - d.getMonth();
  if (m < 0 || (m === 0 && ref.getDate() < d.getDate())) a--;
  return a;
}

export function GuestWaiver() {
  const { profile, fbUser, isAdmin, demoMode } = useAuth();
  const [params] = useSearchParams();
  const bookingId = params.get('booking');
  const cardReturn = params.get('card'); // 'saved' | 'cancel' when Stripe sends us back
  const preview = !bookingId; // owner try-out
  const [booking, setBooking] = useState<BookingDoc | null>(null);
  const [bookingErr, setBookingErr] = useState('');

  useEffect(() => {
    if (!bookingId) return;
    // Demo: the sample booking, never Firestore.
    const load = demoMode
      ? Promise.resolve(DEMO_BOOKINGS.find((x) => x.bookingId === bookingId))
      : db
      ? getDoc(doc(db, 'bookings', bookingId)).then((s) => s.data() as BookingDoc | undefined)
      : null;
    if (!load) return;
    load.then((b) => {
      if (!b || b.lesseeUid !== fbUser?.uid || b.flightPurpose !== 'discovery') setBookingErr('This isn’t one of your discovery bookings.');
      else {
        setBooking(b);
        if (b.waiverId || cardReturn) setStep(CARD_STEP);
        if (b.discoveryGuest) setGuest((g) => ({ ...g, name: g.name || b.discoveryGuest || '' }));
      }
    });
  }, [bookingId, fbUser?.uid, demoMode]);

  // step -1 = hand-off screen, 0 = guest details, 1..N = sections, N+1 = sign, N+2 = done
  // (and the card for the airplane, for a real booking)
  const N = WAIVER.sections.length;
  const CARD_STEP = N + 2;
  const [step, setStep] = useState(-1);
  const [guest, setGuest] = useState({ name: '', dob: '', email: '', phone: '', weight: '', emergency: '', usCitizen: '' as '' | 'yes' | 'no' });
  const [parent, setParent] = useState({ name: '', relationship: '', email: '' });
  const [initials, setInitials] = useState<Record<string, string>>({});
  const [consent, setConsent] = useState(false);
  const [ack, setAck] = useState(false);
  const [sig, setSig] = useState<SignatureResult | null>(null);
  const [parentSig, setParentSig] = useState<SignatureResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [waiverId, setWaiverId] = useState('');
  const [result, setResult] = useState<{ status?: string; pdfUrl?: string; emailedAt?: unknown; emailError?: string | null } | null>(null);

  const age = guest.dob ? ageOn(guest.dob) : null;
  const minor = age !== null && age < 18;
  const cfiName = [profile?.personal?.firstName, profile?.personal?.lastName].filter(Boolean).join(' ') || profile?.email || 'Instructor';

  const detailsOk =
    guest.name.trim().length > 2 &&
    age !== null && age >= 0 && age < 110 &&
    /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(guest.email.trim()) &&
    guest.phone.replace(/\D/g, '').length >= 10 &&
    Number(guest.weight) > 40 && Number(guest.weight) < 500 &&
    guest.emergency.trim().length > 4 &&
    guest.usCitizen !== '' &&
    (!minor || (parent.name.trim().length > 2 && parent.relationship.trim().length > 1 && /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(parent.email.trim())));

  const section = step >= 1 && step <= N ? WAIVER.sections[step - 1] : null;
  const sectionOk = !section || !section.initial || (initials[String(section.n)] ?? '').trim().length >= 2;
  const signOk = consent && ack && sig && (!minor || parentSig);

  // Each screen starts at its top (the app scrolls an inner container, not the window).
  const topRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    topRef.current?.scrollIntoView({ block: 'start' });
  }, [step]);

  useEffect(() => {
    if (!waiverId || !db || demoMode) return;
    return onSnapshot(doc(db, 'discoveryWaivers', waiverId), (s) => setResult(s.data() ?? null));
  }, [waiverId, demoMode]);

  // Card for the airplane (fuel-included discovery flight). Stripe's hosted page, the guest's own customer.
  const [card, setCard] = useState<{ state: 'idle' | 'busy' | 'saved' | 'error'; last4?: string; brand?: string; msg?: string }>({ state: 'idle' });
  // The portal can't call functions directly (org policy), so a card request is a
  // guestCardRequests doc that onGuestCardRequest answers on the same doc.
  async function cardRequest<T>(op: 'session' | 'confirm', extra: Record<string, string> = {}): Promise<T> {
    if (!db || !fbUser || !bookingId) throw new Error('Not signed in.');
    const ref = await addDoc(collection(db, 'guestCardRequests'), { op, bookingId, uid: fbUser.uid, ...extra, createdAt: serverTimestamp() });
    return new Promise<T>((resolve, reject) => {
      const timer = setTimeout(() => { stop(); reject(new Error('That took too long. Try again.')); }, 45000);
      const stop = onSnapshot(ref, (s) => {
        const d = s.data();
        if (d?.status === 'done') { clearTimeout(timer); stop(); resolve(d.result as T); }
        else if (d?.status === 'error') { clearTimeout(timer); stop(); reject(new Error(d.message)); }
      }, (e) => { clearTimeout(timer); reject(e); });
    });
  }
  useEffect(() => {
    if (!bookingId || cardReturn !== 'saved' || demoMode || !fbUser) return;
    setCard({ state: 'busy' });
    cardRequest<{ onFile: boolean; brand?: string; last4?: string }>('confirm')
      .then((r) => setCard(r.onFile ? { state: 'saved', last4: r.last4, brand: r.brand } : { state: 'error', msg: 'Stripe didn’t save a card. Try again.' }))
      .catch((e) => setCard({ state: 'error', msg: e?.message || 'Couldn’t confirm the card. Try again.' }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bookingId, cardReturn, demoMode, fbUser?.uid]);
  async function addCard() {
    if (demoMode) {
      // Demo: stamp the sample booking the way onGuestCardRequest stamps a real one.
      const b = DEMO_BOOKINGS.find((x) => x.bookingId === bookingId);
      if (b) Object.assign(b, { guestCardLast4: '4242', guestCardBrand: 'visa' });
      setCard({ state: 'saved', last4: '4242', brand: 'visa' });
      return;
    }
    if (!bookingId) return;
    setCard({ state: 'busy' });
    try {
      const r = await cardRequest<{ url: string }>('session', { returnPath: isAdmin ? '/admin/discovery/waiver' : '/discovery/waiver' });
      window.location.href = r.url;
    } catch (e) {
      setCard({ state: 'error', msg: (e as Error)?.message || 'Couldn’t open the card page. Try again.' });
    }
  }
  const cardOnFile = card.state === 'saved' ? card : booking?.guestCardLast4 ? { last4: booking.guestCardLast4, brand: booking.guestCardBrand } : null;

  const progress = useMemo(() => Math.max(0, Math.min(1, (step + 1) / (N + 2))), [step, N]);

  async function submit() {
    // Demo: nothing is written or sent. Stamp the sample booking the way the server
    // trigger stamps a real one, and show the emailed state.
    if (demoMode && sig && !busy) {
      const b = DEMO_BOOKINGS.find((x) => x.bookingId === bookingId);
      if (b) {
        b.waiverId = 'demo-waiver';
        b.waiverUsCitizen = guest.usCitizen === 'yes';
      }
      setResult({ status: 'done', emailedAt: true, emailError: null });
      setStep(N + 2);
      return;
    }
    if (!db || !fbUser || !sig || busy) return;
    setBusy(true);
    setErr('');
    try {
      const ref = await addDoc(collection(db, 'discoveryWaivers'), {
        version: WAIVER.version,
        bookingId: bookingId ?? null,
        cfiUid: fbUser.uid,
        cfiName,
        guest: {
          name: guest.name.trim(),
          dob: guest.dob,
          email: guest.email.trim(),
          phone: guest.phone.trim(),
          weight: guest.weight.trim(),
          emergency: guest.emergency.trim(),
          usCitizen: guest.usCitizen === 'yes',
        },
        minor,
        parent: minor ? { name: parent.name.trim(), relationship: parent.relationship.trim(), email: parent.email.trim() } : null,
        initials,
        consentEsign: consent,
        ackRead: ack,
        signaturePng: sig.dataUrl,
        ...(minor && parentSig ? { parentSignaturePng: parentSig.dataUrl } : {}),
        userAgent: navigator.userAgent.slice(0, 300),
        clientSignedAt: new Date().toLocaleString(),
        preview,
        status: 'submitted',
        createdAt: serverTimestamp(),
      });
      setWaiverId(ref.id);
      setStep(N + 2);
    } catch (e) {
      setErr('That didn’t save. Check the connection and try again.');
      console.error(e);
    } finally {
      setBusy(false);
    }
  }

  if (bookingErr) {
    return (
      <div className="card p-6 text-center">
        <p className="text-ink-muted">{bookingErr}</p>
        <Link to="/bookings" className="btn-secondary mt-4 inline-flex">My bookings</Link>
      </div>
    );
  }
  if (preview && !isAdmin) {
    return (
      <div className="card p-6 text-center">
        <p className="text-ink-muted">Open the waiver from your discovery booking under My bookings.</p>
        <Link to="/bookings" className="btn-secondary mt-4 inline-flex">My bookings</Link>
      </div>
    );
  }

  const Nav = ({ next, nextLabel = 'Next', ok = true }: { next: () => void; nextLabel?: string; ok?: boolean }) => (
    <div className="mt-8 flex gap-3 border-t border-slate-200 pt-4 pb-6">
      {step > -1 && (
        <button type="button" className="btn-secondary flex-1" onClick={() => setStep((s) => s - 1)}>
          Back
        </button>
      )}
      <button type="button" className="btn-primary flex-[2]" disabled={!ok || busy} onClick={next}>
        {nextLabel}
      </button>
    </div>
  );

  return (
    <div ref={topRef} className="mx-auto max-w-xl scroll-mt-24 text-[17px] leading-relaxed text-ink">
      {step <= N + 1 && (
        <div className="mb-4">
          <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-brand">
            <span>Discovery flight waiver{preview ? ' · test' : ''}</span>
            {step >= 1 && step <= N && <span className="text-ink-muted">{step} of {N}</span>}
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-slate-100">
            <div className="h-1.5 rounded-full bg-brand transition-all" style={{ width: `${progress * 100}%` }} />
          </div>
        </div>
      )}

      {step === -1 && (
        <>
          <h1 className="font-display text-2xl font-semibold text-navy-900">Hand the phone to your guest</h1>
          {preview && (
            <p className="mt-3 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">
              Test run. Nothing is attached to a booking, and the PDF is stamped TEST. Use your own email to see the copy the
              guest gets.
            </p>
          )}
          <p className="mt-3 text-ink-muted">
            {booking ? `Discovery flight with ${booking.discoveryGuest ?? 'your guest'}. ` : ''}It takes about two minutes. They read
            each part, initial three of them, and sign with a finger. A signed copy is emailed to them right away.
          </p>
          <div className="mt-6 rounded-xl border border-sky-200 bg-sky-50/60 p-4">
            <p className="font-semibold text-navy-900">For the guest</p>
            <p className="mt-1 text-[15px] text-ink-muted">
              This is a release of liability for your introductory flight lesson in N8366X. Please read it carefully. Each
              screen starts with a plain-English line, then the full wording you’re agreeing to.
            </p>
          </div>
          <Nav next={() => setStep(0)} nextLabel="Start" />
        </>
      )}

      {step === 0 && (
        <>
          <h1 className="font-display text-2xl font-semibold text-navy-900">About you</h1>
          <div className="mt-4 space-y-4">
            <Field label="Full name"><input className="input text-base" autoComplete="name" value={guest.name} onChange={(e) => setGuest({ ...guest, name: e.target.value })} /></Field>
            <Field label="Date of birth"><input className="input text-base" type="date" value={guest.dob} onChange={(e) => setGuest({ ...guest, dob: e.target.value })} /></Field>
            <Field label="Email (your signed copy goes here)"><input className="input text-base" type="email" inputMode="email" autoComplete="email" value={guest.email} onChange={(e) => setGuest({ ...guest, email: e.target.value })} /></Field>
            <Field label="Phone"><input className="input text-base" type="tel" inputMode="tel" autoComplete="tel" value={guest.phone} onChange={(e) => setGuest({ ...guest, phone: e.target.value })} /></Field>
            <Field label="Weight (lb), for weight and balance"><input className="input text-base" inputMode="numeric" value={guest.weight} onChange={(e) => setGuest({ ...guest, weight: e.target.value.replace(/[^\d]/g, '') })} /></Field>
            <Field label="Emergency contact (name and phone)"><input className="input text-base" value={guest.emergency} onChange={(e) => setGuest({ ...guest, emergency: e.target.value })} /></Field>
            <Field label="Are you a U.S. citizen or national?">
              <div className="flex gap-3">
                {(['yes', 'no'] as const).map((v) => (
                  <button key={v} type="button" onClick={() => setGuest({ ...guest, usCitizen: v })}
                    className={`flex-1 rounded-lg border px-4 py-3 text-base font-medium ${guest.usCitizen === v ? 'border-brand bg-brand text-white' : 'border-slate-300 text-ink'}`}>
                    {v === 'yes' ? 'Yes' : 'No'}
                  </button>
                ))}
              </div>
            </Field>
            {minor && (
              <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
                <p className="font-semibold text-amber-900">Under 18: a parent or guardian signs too</p>
                <div className="mt-3 space-y-3">
                  <Field label="Parent or guardian’s full name"><input className="input text-base" value={parent.name} onChange={(e) => setParent({ ...parent, name: e.target.value })} /></Field>
                  <Field label="Relationship"><input className="input text-base" placeholder="Mother, father, guardian" value={parent.relationship} onChange={(e) => setParent({ ...parent, relationship: e.target.value })} /></Field>
                  <Field label="Parent’s email (gets a copy and the receipt)"><input className="input text-base" type="email" value={parent.email} onChange={(e) => setParent({ ...parent, email: e.target.value })} /></Field>
                </div>
              </div>
            )}
          </div>
          <Nav next={() => setStep(1)} ok={detailsOk} />
        </>
      )}

      {section && (
        <>
          <h1 className="font-display text-2xl font-semibold text-navy-900">{section.n}. {section.title}</h1>
          {section.n === 1 && (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-[15px] font-semibold text-red-900">
              <Runs runs={WAIVER.warning as Run[]} />
            </p>
          )}
          <div className="mt-4 rounded-xl bg-sky-50 px-4 py-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand">In plain English</p>
            <p className="mt-1 font-medium text-navy-900">{section.summary}</p>
          </div>
          <p className="mt-4 text-xs font-semibold uppercase tracking-wide text-ink-muted">The agreement</p>
          {section.paragraphs.map((p, i) => (
            <p key={i} className="mt-3"><Runs runs={p as Run[]} /></p>
          ))}
          {section.initial && (
            <div className="mt-6 rounded-xl border-2 border-brand/40 p-4">
              <label className="block font-semibold text-navy-900">Initial to confirm you’ve read and accept Section {section.n}</label>
              <input
                className="input mt-2 w-32 text-center text-xl font-semibold uppercase tracking-widest"
                maxLength={4}
                autoCapitalize="characters"
                value={initials[String(section.n)] ?? ''}
                onChange={(e) => setInitials({ ...initials, [String(section.n)]: e.target.value.replace(/[^A-Za-z]/g, '').toUpperCase() })}
              />
            </div>
          )}
          <Nav next={() => setStep(step + 1)} ok={sectionOk} />
        </>
      )}

      {step === N + 1 && (
        <>
          <h1 className="font-display text-2xl font-semibold text-navy-900">Sign</h1>
          <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-5 py-4">
            <p className="text-[13px] font-bold uppercase leading-6 tracking-wide text-navy-900"><Runs runs={WAIVER.acknowledgment as Run[]} /></p>
          </div>
          <div className="mt-5 space-y-3">
            <label className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4">
              <input type="checkbox" className="mt-1 h-6 w-6 shrink-0" checked={ack} onChange={(e) => setAck(e.target.checked)} />
              <span className="text-[16px] leading-7">I have read the whole agreement and had the chance to ask questions.</span>
            </label>
            <label className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white px-5 py-4">
              <input type="checkbox" className="mt-1 h-6 w-6 shrink-0" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span className="text-[16px] leading-7">I agree to sign electronically. My signature and initials here are as binding as signing on paper.</span>
            </label>
          </div>
          <div className="mt-6 rounded-2xl border border-slate-200 bg-white px-5 py-4">
            <p className="mb-3 text-sm font-semibold text-navy-900">{guest.name || 'Participant'}{minor ? ' (participant)' : ''}, sign below</p>
            <SignaturePad onChange={setSig} />
          </div>
          {minor && (
            <div className="mt-4 rounded-2xl border border-slate-200 bg-white px-5 py-4">
              <p className="mb-3 text-sm font-semibold text-navy-900">{parent.name || 'Parent or guardian'} ({parent.relationship || 'parent or guardian'}), sign below</p>
              <SignaturePad onChange={setParentSig} />
            </div>
          )}
          {err && <p className="mt-4 text-sm font-medium text-red-700">{err}</p>}
          <Nav next={submit} nextLabel={busy ? 'Saving…' : 'Sign waiver'} ok={Boolean(signOk)} />
        </>
      )}

      {step === N + 2 && !guest.name && booking && (
        <div className="card p-6 text-center">
          <h1 className="font-display text-2xl font-semibold text-navy-900">
            {booking.waiverGuestName || booking.discoveryGuest || 'Your guest'}’s waiver is signed
          </h1>
          <GuestCardPanel card={card} cardOnFile={cardOnFile} onAdd={addCard} cfiFirst={cfiName.split(' ')[0]} />
          <Link to={isAdmin ? '/admin/discovery' : '/bookings'} className="btn-secondary mt-4 inline-flex">Back to bookings</Link>
        </div>
      )}

      {step === N + 2 && Boolean(guest.name) && (
        <div className="card p-6 text-center">
          <h1 className="font-display text-2xl font-semibold text-navy-900">Signed. Thank you, {guest.name.split(' ')[0]}.</h1>
          <p className="mt-3 text-ink-muted">
            {result?.emailedAt
              ? `Your signed copy was emailed to ${guest.email}.`
              : result?.emailError
              ? 'The waiver is signed and saved, but the email didn’t go out. Your instructor can send it.'
              : `Your signed copy is on its way to ${guest.email}.`}
          </p>
          {result?.pdfUrl && (
            <a href={result.pdfUrl} target="_blank" rel="noreferrer" className="btn-secondary mt-5 inline-flex">
              View signed PDF
            </a>
          )}
          {result?.status === 'error' && <p className="mt-3 text-sm text-red-700">Something went wrong building the PDF. The signature is saved; tell us so we can re-send it.</p>}
          {!preview && bookingId ? (
            <GuestCardPanel card={card} cardOnFile={cardOnFile} onAdd={addCard} cfiFirst={cfiName.split(' ')[0]} />
          ) : (
            <p className="mt-6 text-sm text-ink-muted">Hand the phone back to {cfiName.split(' ')[0]}.</p>
          )}
          {guest.usCitizen === 'no' && (
            <div className="mt-5 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm text-amber-900">
              <strong>For the instructor: not a U.S. citizen.</strong> Discovery flight is fine. Before any lesson toward a certificate, they need TSA approval (Flight Training Security Program). Don't credit this flight toward a certificate.
            </div>
          )}
          <Link to={isAdmin ? '/admin/discovery' : '/bookings'} className="btn-primary mt-4 inline-flex">Done</Link>
        </div>
      )}
    </div>
  );
}

// The card for the airplane: $170 per Hobbs hour, fuel included, charged when the flight is
// logged. The guest (or parent) enters it on Stripe's page; we only ever see the last 4.
function GuestCardPanel({
  card,
  cardOnFile,
  onAdd,
  cfiFirst,
}: {
  card: { state: 'idle' | 'busy' | 'saved' | 'error'; msg?: string };
  cardOnFile: { last4?: string; brand?: string } | null;
  onAdd: () => void;
  cfiFirst: string;
}) {
  if (cardOnFile?.last4)
    return (
      <div className="mt-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-left text-sm text-emerald-900">
        <strong>Card saved for the airplane:</strong> {(cardOnFile.brand || 'card').toUpperCase()} ending {cardOnFile.last4}. It’s charged after
        the flight, by the Hobbs hour. Hand the phone back to {cfiFirst}.
      </div>
    );
  return (
    <div className="mt-6 rounded-xl border border-sky-200 bg-sky-50/60 p-4 text-left">
      <p className="font-semibold text-navy-900">One more step: a card for the airplane</p>
      <p className="mt-1 text-[15px] text-ink-muted">
        You’re renting the airplane for your lesson: <strong>$170 per Hobbs hour, fuel included</strong>, charged to this card once your flight is logged. A
        typical discovery flight runs about an hour on the Hobbs. Your instructor’s fee is separate and paid to them directly.
      </p>
      <p className="mt-2 text-xs text-ink-muted">
        You’ll enter the card on Stripe’s secure page, then come right back here. Under 18? Your parent or guardian enters their own
        card. If the airplane doesn’t take off, nothing is charged.
      </p>
      {card.state === 'error' && <p className="mt-2 text-sm text-red-700">{card.msg}</p>}
      <button type="button" className="btn-primary mt-4 w-full" disabled={card.state === 'busy'} onClick={onAdd}>
        {card.state === 'busy' ? 'One moment…' : 'Add card'}
      </button>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="label">{label}</label>
      {children}
    </div>
  );
}
